import { Autocomplete, TextField, ListItem, Typography, Tooltip } from '@mui/material';
import { useState, useEffect, SyntheticEvent } from 'react';
import './NewOptionAutocomplete.css';
import useDebounce from '../../shared/hooks/useDebounce';
import useRuleValidation from '../../shared/hooks/useRuleValidation';
import PaginationParams from '../../models/PaginationParams';
import { NewOptionAutocompleteOption, NewOptionAutocompleteOptionClass, NewOptionAutocompleteProps } from './NewOptionAutocompleteProps'
import { t } from 'i18next';
import CircularProgress from '@mui/material/CircularProgress';
import CustomArrowIcon from '../CustomArrowIcon/CustomArrowIcon';

const NewOptionAutocomplete = ({
  options,
  value,
  placeholder,
  label,
  disableCondition,
  noOptionsText,
  optionNotFoundText,
  alertMessage,
  conditionToShowAlertMessage,
  required,
  setValue,
  onChange,
  fetchFunc,
  maxLength,
  uppercase,
  id, 
  testId
}: NewOptionAutocompleteProps) => {
  const [pattern, setPattern] = useState('');
  const [patternDebounced, isDebouncing] = useDebounce(pattern, 300);
  const [hideOptions, setHideOptions] = useState(false);

  const defaultRule = useRuleValidation(() => !!value);

  const configParams: PaginationParams = {
    pageSize: 20,
    offset: 0
  };

  useEffect(() => {
    if (!value && options.length > 0) {
      setHideOptions(true);
    }
  }, [value]);

  useEffect(() => {
    if (patternDebounced.trim() === '') {
      setHideOptions(true);
    } else {
      setHideOptions(false);
      const params = { ...configParams, pattern: patternDebounced };
      fetchFunc(params);
    }
  }, [patternDebounced]);

  const changePattern = (event: React.ChangeEvent<HTMLInputElement>) => {
    const pattern = event.target.value;
    setPattern(pattern);
  };

  const onChangeAutocomplete = (value?: NewOptionAutocompleteOption | null, reason?: string) => {
    if (reason === 'clear') {
      setHideOptions(true);
      setPattern('');
    }
    if (typeof value !== 'undefined') {
      onChange?.();
    }
  };
  const lookingMatches = t('lookingMatches');
  return (
    <>
      <Autocomplete
        popupIcon={<CustomArrowIcon testId={`${testId}select-arrow`} />}
        role={'autocomplete'}
        fullWidth
        size="small"
        autoHighlight
        options={hideOptions ? [] : options}
        noOptionsText={(pattern && isDebouncing) ? lookingMatches : noOptionsText}
        getOptionLabel={(option: NewOptionAutocompleteOption) => {
          if (typeof option === 'string') {
            return option;
          }
          if (option.inputValue) {
            return option.inputValue;
          }
          return option.name;
        }}
        value={value}
        disabled={disableCondition}
        onChange={(_event: SyntheticEvent, newValue: NewOptionAutocompleteOptionClass | null, reason) => {
          if (typeof newValue === 'string') {
            setValue(new NewOptionAutocompleteOptionClass(undefined, undefined, newValue, undefined));
          } else {
            if (newValue) {
              newValue.removeName = true;
            }
            setValue(newValue);
          }
          onChangeAutocomplete(newValue, reason);
        }}
        filterOptions={(options, params) => {
          const filtered = options;
          let { inputValue } = params
          inputValue = inputValue.toUpperCase()
          return filtered;
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        renderOption={(props, option: NewOptionAutocompleteOption) => <ListItem sx={{ paddingLeft: '5px' }} {...props} key={option?.id}>
          {option.name} {option.shortName && ` (${option.shortName})`}
        </ListItem>}
        renderInput={(params) => (
          <div style={{ position: 'relative' }}>
            <Tooltip title={label}>
              <TextField
                {...params}
                required={required}
                className='REQXXI-NewOptionAutocomplete-TextField'
                fullWidth
                value={pattern}
                label={label}
                placeholder={placeholder}
                onChange={changePattern}
                onBlur={() => setPattern('')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {defaultRule.adornment}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
                inputProps={{
                  ...params.inputProps,
                  maxLength,
                  id:id,
                  style: {
                    ...(uppercase ? { textTransform: 'uppercase' } : {})
                  },
                  'data-testid': `${testId}input`
                }}
              />
            </Tooltip>
            {(pattern && isDebouncing) && (
              <div style={{ position: 'absolute', top: '58%', right: '70px', transform: 'translateY(-50%)' }}>
                <CircularProgress color="primary" size={20} />
              </div>
            )}
          </div>
        )}
      />
      {(alertMessage && conditionToShowAlertMessage) &&
        <Typography variant="subtitle2" className="REQXXI-NewOptionAutocomplete-AlertMessage"> {alertMessage} </Typography>
      }
    </>
  );
};

export default NewOptionAutocomplete;
